import React, { useState, useEffect, useCallback } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import 'react-toastify/dist/ReactToastify.css';

// Field name to display label mapping
const FIELD_LABEL_MAPPING: { [key: string]: string } = {
  penalties: 'Penalties',
  possessionTime: 'Possession Time',
  shots: 'Shots',
  groundBalls: 'Ground Balls',
  turnovers: 'Turnovers / Caused Turnovers',
  clears: 'Clears / Rides',
  faceOffs: 'Face-offs',
};

interface TrackingOption {
  field: string;
  label: string;
  enabled: boolean;
}

const RolesPage: React.FC = () => {
  const [trackingOptions, setTrackingOptions] = useState<TrackingOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch tracking options for the current user for this season
  const fetchTrackingOptions = useCallback(async () => {
    const seasonId = sessionManager.getSeasonId();
    const clientId = sessionManager.getClientId();
    const user = sessionManager.getUser();
    const userId = user?.id;

    if (!seasonId) {
      showErrorMsg('Select a season to get started');
      return;
    }

    if (!userId) {
      showErrorMsg('User session not found');
      return;
    }

    try {
      setLoading(true);

      const response = await api.get(API_URLS.GET_USER_TRACKING_ROLES, {
        params: { userId, seasonId, clientId },
      });

      if (response.data.success) {
        const data = response.data.data;
        const turnoversEnabled = !!data.turnovers;
        // Older roles only had combined turnovers; keep GBs enabled with them until reconfigured
        const groundBallsEnabled =
          data.groundBalls !== undefined ? !!data.groundBalls : turnoversEnabled;
        const normalizedData: { [key: string]: boolean } = {
          ...data,
          groundBalls: groundBallsEnabled,
          turnovers: turnoversEnabled,
        };
        const options: TrackingOption[] = Object.keys(FIELD_LABEL_MAPPING).map((field) => ({
          field,
          label: FIELD_LABEL_MAPPING[field],
          enabled: !!normalizedData[field],
        }));
        setTrackingOptions(options);
      } else {
        showErrorMsg(response.data.message || 'Failed to fetch tracking options');
      }
    } catch (error: any) {
      showErrorMsg('Failed to fetch tracking options. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrackingOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (field: string) => {
    setTrackingOptions((prev) =>
      prev.map((option) => (option.field === field ? { ...option, enabled: !option.enabled } : option)),
    );
  };

  const handleSave = async () => {
    const seasonId = sessionManager.getSeasonId();
    const clientId = sessionManager.getClientId();
    const user = sessionManager.getUser();
    const userId = user?.id;

    if (!seasonId) {
      showErrorMsg('Select a season to get started');
      return;
    }

    if (!userId) {
      showErrorMsg('User session not found');
      return;
    }

    try {
      setSaving(true);
      // Prepare the request body with all checkbox statuses
      const requestBody: any = {
        userId,
        seasonId,
        clientId,
      };
      trackingOptions.forEach((option) => {
        requestBody[option.field] = option.enabled;
      });
      const response = await api.post(API_URLS.UPDATE_USER_TRACKING_ROLES, requestBody);
      if (response.data.success) {
        showSuccessMsg('Your tracking preferences updated successfully!');
      } else {
        showErrorMsg(response.data.message || 'Failed to update tracking options');
      }
    } catch (error: any) {
      console.error('Error saving tracking options:', error);
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else {
        showErrorMsg('Failed to save tracking options. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full relative p-4">
      {/* Title and Description */}
      <div className="border border-[#bccedf]">
        <div className="relative flex flex-col gap-1 justify-between">
          {/* Up arrow */}
          <div className="flex justify-center w-full my-2 z-10">
            <img src="/icons/icon-up.png" alt="up" className="w-12 h-12" />
          </div>
          {/* Title */}
          <div className="relative text-[30px] pl-4 font-bold text-start z-10 mt-[-30px]">Stat Tracking Roles</div>
          <div className="relative text-[20px] pl-4 flex w-full justify-between items-stretch gap-4 z-10 border-b border-[#bccedf] pb-8 ">
            Choose your personal tracking roles for this season below. Each stat taker can configure their own
            preferences. Select the statistics you want to track during games.
          </div>
        </div>
        {/* Action Cards */}
        <div className="flex flex-col gap-2 my-6">
          <div className="flex flex-col items-center w-full py-8 px-2">
            <div className="w-full px-4">
              <div className="bg-white rounded-lg shadow-lg p-0">
                <div className="bg-[#163a5c] text-white text-xl font-bold px-6 py-3 border-b border-[#e5e7eb]">
                  My Tracking Roles
                </div>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-lg">Loading tracking options...</div>
                  </div>
                ) : trackingOptions.length === 0 ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-lg text-gray-500">No tracking options available for this season</div>
                  </div>
                ) : (
                  <div className="flex flex-col divide-y divide-[#7fe526]">
                    {trackingOptions.map((option, idx) => (
                      <label
                        key={option.field}
                        className={`flex items-center justify-between px-6 py-3 text-lg font-medium ${
                          idx === trackingOptions.length - 1 ? 'rounded-b-lg' : ''
                        }`}
                        style={{ background: '#fff' }}
                      >
                        <span>{option.label}</span>
                        <input
                          type="checkbox"
                          checked={option.enabled}
                          onChange={() => handleToggle(option.field)}
                          className="peer sr-only"
                        />
                        <span
                          className={`w-6 h-6 border-2 border-[#b5e6a7] rounded flex items-center justify-center transition-colors ${
                            option.enabled ? 'bg-[#7fe526]' : 'bg-white'
                          }`}
                        >
                          {option.enabled && (
                            <svg width="18" height="18" viewBox="0 0 18 18" className="pointer-events-none">
                              <polyline
                                points="4.5 9.5 8 13 13.5 6"
                                fill="none"
                                stroke="#19133c"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Save Section */}
        <div className="relative flex flex-col gap-1 justify-between border-t border-[#edf5fc]">
          <div className="h-[1px] w-full bg-[#bccedf]" />
          <div className="flex justify-center items-center w-full py-4 z-10">
            <button
              className="bg-[#7fe526] text-black font-semibold rounded-lg py-3 text-2xl shadow-md transition-all w-48 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Updates'}
            </button>
          </div>
          <div className="flex justify-center w-full mt-8 mb-6 z-10">
            <img src="/icons/icon-down.png" alt="up" className="w-12 h-12" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
